import React from "react";
import { cn } from "./ui/utils";
import { maxPlatform } from "../services/maxPlatform";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "danger" | "outline";
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  icon?: React.ReactNode;
  loading?: boolean;
  haptic?: "light" | "medium" | "heavy" | false;
}

export function Button({
  children,
  className,
  variant = "primary",
  size = "md",
  fullWidth,
  icon,
  loading,
  haptic = "light",
  disabled,
  onClick,
  ...props
}: ButtonProps) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (haptic && !disabled) maxPlatform.hapticFeedback(haptic);
    onClick?.(e);
  };

  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-xl transition-all duration-200 active:scale-[0.97] disabled:opacity-50 disabled:pointer-events-none",
        // Variants
        variant === "primary" &&
          "bg-indigo-500 text-white shadow-lg shadow-indigo-500/20 active:bg-indigo-600",
        variant === "secondary" &&
          "bg-indigo-50 text-indigo-600 active:bg-indigo-100",
        variant === "ghost" &&
          "bg-transparent text-gray-600 active:bg-gray-100",
        variant === "danger" &&
          "bg-red-500 text-white shadow-lg shadow-red-500/20 active:bg-red-600",
        variant === "outline" &&
          "bg-white border border-gray-200 text-gray-700 active:bg-gray-50",
        // Sizes
        size === "sm" && "h-8 px-3 text-[12px]",
        size === "md" && "h-10 px-4 text-[14px]",
        size === "lg" && "h-12 px-6 text-[15px]",
        // Full width
        fullWidth && "w-full",
        className
      )}
      disabled={disabled || loading}
      onClick={handleClick}
      {...props}
    >
      {loading ? (
        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : (
        icon
      )}
      {children}
    </button>
  );
}
